import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import {
  parseToolRefs,
  type ToolRef,
  type ToolRefAction,
  toolRefKey,
  toolResultRefs,
} from '@crowdex/plugin-contracts';

/**
 * What a conversation touched, from its tool results (`brand/crow-extensions.html`, "Ce qu'une
 * conversation touche"). Derived from the messages, never stored on its own: the transcript is
 * already persisted and synced (cloud, desktop, phone), a ref travels with the step that made it
 * (its `toolMetadata.crowdex.refs`, which code mode writes from the result's
 * `_meta["crowdex/refs"]`, or the step's output itself), and rewinding or editing a turn drops
 * its refs with it. No table, no migration, nothing to keep in step.
 */

/** A ref with the message it first appeared in. */
export type ConversationRef = { ref: ToolRef; messageId: string };

/** How strongly a step touched something: what it made outweighs what it only read. */
const ACTION_WEIGHT: Record<ToolRefAction, number> = { read: 0, sent: 1, changed: 2, made: 3 };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Two sightings of the same ref: the later one's state, the stronger action. */
function mergeRef(earlier: ToolRef, later: ToolRef): ToolRef {
  const action =
    earlier.action === undefined
      ? later.action
      : later.action === undefined || ACTION_WEIGHT[earlier.action] > ACTION_WEIGHT[later.action]
        ? earlier.action
        : later.action;
  return { ...earlier, ...later, ...(action === undefined ? {} : { action }) };
}

/** The refs of one part: a finished tool step's (its metadata, its output). */
export function partRefs(part: CrowdexUIMessage['parts'][number]): ToolRef[] {
  if (part.type !== 'dynamic-tool' && !part.type.startsWith('tool-')) return [];
  const step = part as { state?: unknown; toolMetadata?: unknown; output?: unknown };
  if (step.state !== 'output-available') return [];
  const crowdex = isRecord(step.toolMetadata) ? step.toolMetadata['crowdex'] : undefined;
  const refs = [
    ...parseToolRefs(isRecord(crowdex) ? crowdex['refs'] : undefined),
    ...toolResultRefs(step.output),
  ];
  return dedupe(refs);
}

function dedupe(refs: readonly ToolRef[]): ToolRef[] {
  const found = new Map<string, ToolRef>();
  for (const ref of refs) {
    const key = toolRefKey(ref);
    const known = found.get(key);
    found.set(key, known === undefined ? ref : mergeRef(known, ref));
  }
  return [...found.values()];
}

/** What one message's steps touched, in the order it touched them, each once. */
export function messageRefs(message: CrowdexUIMessage): ToolRef[] {
  if (message.role !== 'assistant') return [];
  return dedupe(message.parts.flatMap(partRefs));
}

/**
 * What the whole conversation touched, each once: where it first appeared, its latest state
 * (a pull request merged since), and the most it did with it (made over read).
 */
export function conversationRefs(messages: readonly CrowdexUIMessage[]): ConversationRef[] {
  const found = new Map<string, ConversationRef>();
  for (const message of messages) {
    for (const ref of messageRefs(message)) {
      const key = toolRefKey(ref);
      const known = found.get(key);
      found.set(
        key,
        known === undefined
          ? { ref, messageId: message.id }
          : { ref: mergeRef(known.ref, ref), messageId: known.messageId },
      );
    }
  }
  return [...found.values()];
}

/** The artifact of the index a ref names: by its id, else its space and path. */
export function artifactOfRef(
  ref: ToolRef,
  artifacts: readonly ArtifactListingEntry[],
): ArtifactListingEntry | undefined {
  if (ref.kind !== 'artifact') return undefined;
  const path = ref.path?.replace(/^\.?\//, '');
  return (
    artifacts.find((artifact) => artifact.id === ref.id) ??
    (path === undefined
      ? undefined
      : artifacts.find(
          (artifact) =>
            artifact.path === path &&
            (ref.space === undefined || artifact.space === ref.space) &&
            artifact.plugin === ref.app,
        ))
  );
}

/**
 * Where a ref belongs in the conversation (brand "Trois sortes"): an artifact (its editor), an
 * item of an app or an editor (docked in the side panel), a service's result (opened at the
 * service ↗, no panel), or an assistant's work.
 */
export type RefPlace = 'artifact' | 'app' | 'service' | 'work';

export function refPlace(ref: ToolRef, dockable: (app: string) => boolean): RefPlace {
  if (ref.kind === 'work') return 'work';
  if (ref.kind === 'artifact') return 'artifact';
  return dockable(ref.app) ? 'app' : 'service';
}

/** "Made", "Changed", "Read", "Sent". */
export function refActionLabel(action: ToolRefAction | undefined): string | null {
  if (action === undefined) return null;
  return { read: 'Read', made: 'Made', changed: 'Changed', sent: 'Sent' }[action];
}

/** An app's name from its reverse-domain name when the user has not installed it. */
export function appNameOf(app: string): string {
  if (app === 'crowdex') return 'Crowdex';
  const last = app.split('.').at(-1) ?? app;
  return KNOWN_NAMES[last] ?? last.charAt(0).toUpperCase() + last.slice(1);
}

/** The names Crowlingo's applications and the usual services write themselves with. */
const KNOWN_NAMES: Record<string, string> = {
  crowdrive: 'CrowDrive',
  crowbox: 'CrowBox',
  crowroom: 'CrowRoom',
  crowpus: 'Crowpus',
  github: 'GitHub',
  gitlab: 'GitLab',
};
